/* eslint-disable react-hooks/exhaustive-deps */
import { useTranslation } from 'next-i18next'
import React, { useEffect, useState } from 'react'
import { Select, Form, Button, Divider } from 'antd'
import { useAppDispatch, useAppSelector } from 'src/app/hooks'
import { getFormatedPrice, getProjectUnit } from 'src/core/helpers'
import { Currency, MakeCounterOfferPayload, UserSaleOffer } from 'src/core/models'
import { counterSaleOffferSelector, currencyListSelector, makeCounterSaleOffer } from 'src/features'
import { CustomModal, CustomInputNumber, CustomInputDecimal, CustomSelect, CustomText } from 'src/components'

interface MakeCounterOfferModalProps {
  isModalVisible: boolean
  handleCancel: () => void
  saleOffer: UserSaleOffer
}

const MakeCounterOfferModal: React.FC<MakeCounterOfferModalProps> = (props) => {
  const { isModalVisible, handleCancel, saleOffer } = props
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const dispatch = useAppDispatch()
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>(null)
  const [selectedAmount, setSelectedAmount] = useState<number>(null)
  const [selectedPrice, setSelectedPrice] = useState<number>(null)
  const [visualTotal, setVisualTotal] = useState<number>(0)
  const currencyList = useAppSelector(currencyListSelector)
  const {
    loading: loadingCounter,
    success: successCounterOffer
  } = useAppSelector(counterSaleOffferSelector)

  const handleModalClose = () => {
    setDefaultvalues()
    handleCancel()
  }

  const handleSubmit = (values: MakeCounterOfferPayload) => {
    dispatch(makeCounterSaleOffer(saleOffer.id, values))
  }

  useEffect(() => { setDefaultvalues() }, [form, saleOffer])

  useEffect(() => {
    if (successCounterOffer) { handleModalClose() }
  }, [successCounterOffer])

  useEffect(() => {
    setVisualTotal(selectedAmount * selectedPrice)
  }, [selectedAmount, selectedPrice])

  const setDefaultvalues = () => {
    form.setFieldsValue({
      ['amount']: saleOffer.amount,
      ['price_per_unit']: saleOffer.price_per_unit,
      ['currency']: saleOffer.currency,
    })
    setSelectedCurrency(currencyList?.find(c => saleOffer?.currency === c.id))
    setSelectedAmount(saleOffer.amount)
    setSelectedPrice(saleOffer.price_per_unit)
  }

  return <CustomModal
    title={t('SalesOffer.MakeCounter')}
    footer={null}
    open={isModalVisible}
    onCancel={handleModalClose}
    width={430}
  >
    <Form form={form} onFinish={handleSubmit}>
      {/* Amount */}
      <CustomText label={t('SalesOffer.NewAmount')} />
      <CustomInputNumber
        name='amount'
        placeholder={t('SalesOffer.AmountPlaceholder')}
        addonAfter={<CustomText label={getProjectUnit(saleOffer.project_kind)} />}
        onChange={(value) => setSelectedAmount(value)}
        required
      />

      {/* Price */}
      <CustomText label={t('SalesOffer.NewPrice')} />
      <CustomInputDecimal
        name='price_per_unit'
        placeholder={t('SalesOffer.PricePlaceHolder')}
        addonAfter={<CustomText label={selectedCurrency?.symbol} />}
        onChange={(value) => setSelectedPrice(value as number)}
        required
      />

      {/* Currency */}
      <CustomText label={t('SalesOffer.NewCurrency')} />
      <CustomSelect
        name='currency'
        placeholder={t('RequestProposal.CurrencyPlaceHolder')}
        showSearch={false}
        onChange={(value: number) => setSelectedCurrency(currencyList?.find(c => c.id === value))}
        required
      >
        {currencyList.map(currency => (
          <Select.Option key={currency.id} value={currency.id}>
            {currency.code} {currency.symbol}
          </Select.Option>
        ))}
      </CustomSelect>

      {/* Total Label */}
      <div className='float-right'>
        <CustomText
          label={`Total ${getFormatedPrice(visualTotal, selectedCurrency?.code)}`}
        />
      </div>

      {/* Submit Button */}
      <Divider />
      <Button
        block
        type='primary'
        htmlType='submit'
        size='large'
        loading={loadingCounter}
        disabled={loadingCounter}
        className='radius-100'
      >
        {loadingCounter ? t('SalesOffer.MakingCounter') : t('SalesOffer.SubmitCounter')}
      </Button>
    </Form>
  </CustomModal>
}

export default MakeCounterOfferModal
